<template>
  <header id="app-header">
    <template v-if="estUtilisateurAuthentifie">
      <BasicButton
        class="sidebar-button tablet-none desktop-none"
        :icon-name="isOpenMobileSidebarLeft ? 'menu-grid' : 'menu-grid-reduced'"
        @click="toggleMobileSidebar('left')"
        iconActive
        size="large"
      />
      <div class="left-content">
        <div class="logo-connected">
          <BasicIcon name="mon-espace" />
        </div>
        <div class="left-content__text">
          <BasicText
            size="h3"
            weight="bold"
            color="white"
            wrapAll
          >
            {{ headerText?.titre }}
          </BasicText>
          <BasicText
            v-if="headerText?.sousTitre"
            size="body-xl"
            color="white"
            wrapAll
          >
            {{ headerText?.sousTitre }}
          </BasicText>
        </div>
      </div>
      <BasicButton
        class="sidebar-button-right tablet-none desktop-none"
        :class="{ 'sidebar-button-right--is-open': isOpenMobileSidebarRight }"
        @click="toggleMobileSidebar('right')"
        size="large"
      >
        <div class="context__icon">
          <BasicIcon
            v-focusable="{
              onEnter: () => (showMenu = !showMenu),
            }"
            :name="currentContextIconName"
            @click="showMenu = !showMenu"
            class="tablet-none desktop-none"
          />
        </div>
        <span class="burger">
          <span class="bar" />
          <span class="bar" />
          <span class="bar" />
        </span>
        <span
          v-if="unreadNotificationsList?.data.length && !isOpenMobileSidebarRight"
          class="sidebar-button-right__pastille"
        />
      </BasicButton>
    </template>
    <div
      class="disconnected"
      v-if="!estAuthentificationEnCours && !estUtilisateurAuthentifie"
    >
      <div class="disconnected__logo">
        <img
          src="@/assets/logo-akto-blanc.png"
          alt="Logo AKTO"
        />
      </div>
      <div class="disconnected__buttons">
        <BasicButton
          label="Créer son compte"
          variant="outlined"
          size="large"
          @click="inscriptionAsync"
          class="disconnected__create-account"
        />
        <BasicButton
          label="Se connecter"
          size="large"
          @click="connexionAsync"
        />
      </div>
    </div>
    <div
      v-if="estUtilisateurAuthentifie"
      class="right-content"
    >
      <HeaderComboContexte />
      <NotificationsComponent>
        <template #trigger-notification="{ toggleNotifications, hasNotifications }">
          <BasicButton
            iconName="notification"
            @click="toggleNotifications"
            size="large"
          />
          <span
            v-if="hasNotifications"
            class="notifications__pastille"
          />
        </template>
      </NotificationsComponent>
      <div
        class="user-infos"
        ref="menu"
      >
        <BasicTooltip
          label="Changer de compte"
          position="top"
        >
          <BasicText
            size="h5"
            weight="bold"
            color="white"
            @click="showMenu = !showMenu"
            focusable
            pointer
            nb-max-lines="2"
            class="mobile-none tablet-none"
          >
            {{ fullName }}
          </BasicText>
          <BasicAvatar
            v-focusable="{
              onEnter: () => (showMenu = !showMenu),
            }"
            type="name"
            :name="fullName"
            avatarSize="large"
            @click="showMenu = !showMenu"
            pointer
            class="mobile-none desktop-none"
          />
        </BasicTooltip>
        <div
          class="menu"
          v-if="showMenu"
        >
          <template v-if="estUtilisateurImpersonnalise">
            <div class="menu__imperso">
              <BasicIcon
                name="profile"
                class="icon"
                color="secondary-600"
                active
              />
              <BasicText
                color="secondary-600"
                size="body-xl"
                weight="bold"
              >
                {{ fullNameInterne }}
              </BasicText>
            </div>
            <BasicText
              class="link"
              color="secondary-600"
              @click="changerUtilisateur"
              pointer
            >
              Changer d’utilisateur externe
            </BasicText>
            <hr />
          </template>
          <template v-if="!estAuthentificationEnCours">
            <BasicLink
              @link-click="redirectionProfil"
              type="dark"
              label="Accéder à mon profil"
            />
            <BasicLink
              @link-click="demanderRattachement"
              type="dark"
              label="Me rattacher à une entreprise"
            />
          </template>
          <BasicLink
            @link-click="deconnexionAsync"
            type="dark"
            label="Se déconnecter"
          />
        </div>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
  import { storeToRefs } from 'pinia'
  import { useRoute } from 'vue-router'
  import { computed, ref } from 'vue'
  import { useSidebarStateStore } from '@/features/interface/layout/sideBar/useSideBarStore'
  import HeaderComboContexte from '@/features/interface/layout/comboContexte/ComboContexte.vue'
  import NotificationsComponent from '../notifications/NotificationsComponent.vue'
  import { useNavigationAction } from '@/features/interface/layout/header/useNavigationAction'
  import { useNotificationsStore } from '../notifications/useNotificationsStore'
  import { useHandleClickOutside } from '../../composables'
  import { useAppStateStore } from '@/features/application/state/useAppStateStore'
  import { TypesContextesAutorisesEnum } from '@/features/auth/context-app/types/ContextApp'
  import { isFunction } from '@/features/shared/tools/function.ts'

  const route = useRoute()

  const appState = useAppStateStore()

  const menu = ref<HTMLElement | null>(null)
  const showMenu = ref(false)

  const notificationsStore = useNotificationsStore()

  const { unreadNotificationsList } = storeToRefs(notificationsStore)

  const navigationAction = useNavigationAction(() => {
    showMenu.value = false
  })

  const { demanderRattachement, changerUtilisateur, redirectionProfil, fullName, fullNameInterne } = navigationAction
  const { inscriptionAsync, connexionAsync, deconnexionAsync } = navigationAction.appState
  const { estUtilisateurImpersonnalise, estAuthentificationEnCours, estUtilisateurAuthentifie } = storeToRefs(
    navigationAction.appState,
  )

  const currentContextIconName = computed(() =>
    appState.contextCourant === TypesContextesAutorisesEnum.Prestataire ? 'prestataire' : 'adherent',
  )

  const sidebarStateStore = useSidebarStateStore()
  const { toggleMobileSidebar } = sidebarStateStore
  const { isOpenMobileSidebarLeft, isOpenMobileSidebarRight } = storeToRefs(sidebarStateStore)

  const headerText = computed<{ titre: string; sousTitre?: string } | undefined>(() => {
    let result: string | [string, string] | undefined
    if (isFunction(route.meta.label)) result = route.meta.label(route)
    else result = route.meta.label ?? ''

    if (!result) return
    else if (typeof result === 'string') return { titre: result }
    else return { titre: result[0], sousTitre: result[1] }
  })

  useHandleClickOutside(menu, () => {
    showMenu.value = false
  })
</script>

<style scoped lang="less">
  @import './HeaderApp.less';
</style>
